import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { RequirePermission } from '@/components/auth/require-permission';
import { AppShell } from '@/components/layout/app-shell';
import { Skeleton } from '@/components/ui/skeleton';

/*
 * Everything except the shell and the login screen is code-split. The console
 * is opened cold every morning, so the first paint should not carry the
 * webhook tester or the chart library with it.
 */
const LoginPage = lazy(() => import('@/pages/login'));
const DashboardPage = lazy(() => import('@/pages/dashboard'));
const SessionsPage = lazy(() => import('@/pages/sessions/sessions-page'));
const SessionDetailPage = lazy(() => import('@/pages/sessions/session-detail'));
const InboxPage = lazy(() => import('@/pages/inbox'));
const ChatsPage = lazy(() => import('@/pages/chats/chats-page'));
const ChatDetailPage = lazy(() => import('@/pages/chats/chat-detail'));
const ContactsPage = lazy(() => import('@/pages/contacts'));
const WebhooksPage = lazy(() => import('@/pages/webhooks/webhooks-page'));
const WebhookDetailPage = lazy(() => import('@/pages/webhooks/webhook-detail'));
const KeywordsPage = lazy(() => import('@/pages/keywords'));
const AiPage = lazy(() => import('@/pages/ai'));
const NotificationsPage = lazy(() => import('@/pages/notifications'));
const BankTransactionsPage = lazy(() => import('@/pages/banking/transactions-page'));
const BankRulesPage = lazy(() => import('@/pages/banking/rules-page'));
const ApiKeysPage = lazy(() => import('@/pages/api-keys'));
const UsersPage = lazy(() => import('@/pages/users'));
const RolesPage = lazy(() => import('@/pages/roles'));
const LogsPage = lazy(() => import('@/pages/logs'));
const AuditPage = lazy(() => import('@/pages/audit'));
const SettingsPage = lazy(() => import('@/pages/settings'));
const NotFoundPage = lazy(() => import('@/pages/not-found'));

function RouteFallback() {
  return (
    <div className="space-y-3 p-5">
      <Skeleton className="h-6 w-56" />
      <Skeleton className="h-4 w-80" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

const withSuspense = (node: React.ReactNode) => (
  <Suspense fallback={<RouteFallback />}>{node}</Suspense>
);

const router = createBrowserRouter([
  { path: '/login', element: withSuspense(<LoginPage />) },
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: withSuspense(
          <RequirePermission permission="dashboard.view"><DashboardPage /></RequirePermission>,
        ) },
      { path: 'sessions', element: withSuspense(
          <RequirePermission permission="sessions.view"><SessionsPage /></RequirePermission>,
        ) },
      { path: 'sessions/:id', element: withSuspense(
          <RequirePermission permission="sessions.view"><SessionDetailPage /></RequirePermission>,
        ) },
      { path: 'inbox', element: withSuspense(
          <RequirePermission permission="messages.view"><InboxPage /></RequirePermission>,
        ) },
      { path: 'chats', element: withSuspense(
          <RequirePermission permission="chats.view"><ChatsPage /></RequirePermission>,
        ) },
      { path: 'chats/:id', element: withSuspense(
          <RequirePermission permission="chats.view"><ChatDetailPage /></RequirePermission>,
        ) },
      { path: 'contacts', element: withSuspense(
          <RequirePermission permission="contacts.view"><ContactsPage /></RequirePermission>,
        ) },
      { path: 'webhooks', element: withSuspense(
          <RequirePermission permission="webhooks.view"><WebhooksPage /></RequirePermission>,
        ) },
      { path: 'webhooks/:id', element: withSuspense(
          <RequirePermission permission="webhooks.view"><WebhookDetailPage /></RequirePermission>,
        ) },
      { path: 'keywords', element: withSuspense(
          <RequirePermission permission="keywords.manage"><KeywordsPage /></RequirePermission>,
        ) },
      { path: 'ai', element: withSuspense(
          <RequirePermission permission="ai.manage"><AiPage /></RequirePermission>,
        ) },
      { path: 'notifications', element: withSuspense(
          <RequirePermission permission="notifications.manage"><NotificationsPage /></RequirePermission>,
        ) },
      { path: 'banking/transactions', element: withSuspense(
          <RequirePermission permission="banking.view"><BankTransactionsPage /></RequirePermission>,
        ) },
      { path: 'banking/rules', element: withSuspense(
          <RequirePermission permission="banking.manage"><BankRulesPage /></RequirePermission>,
        ) },
      { path: 'api-keys', element: withSuspense(
          <RequirePermission permission="apikeys.manage"><ApiKeysPage /></RequirePermission>,
        ) },
      { path: 'users', element: withSuspense(
          <RequirePermission permission="users.manage"><UsersPage /></RequirePermission>,
        ) },
      { path: 'roles', element: withSuspense(
          <RequirePermission permission="roles.manage"><RolesPage /></RequirePermission>,
        ) },
      { path: 'logs', element: withSuspense(
          <RequirePermission permission="logs.view"><LogsPage /></RequirePermission>,
        ) },
      { path: 'audit', element: withSuspense(
          <RequirePermission permission="audit.view"><AuditPage /></RequirePermission>,
        ) },
      { path: 'settings', element: withSuspense(
          <RequirePermission permission="settings.manage"><SettingsPage /></RequirePermission>,
        ) },
      { path: '*', element: withSuspense(<NotFoundPage />) },
    ],
  },
]);

export function Router() {
  return <RouterProvider router={router} />;
}
